import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Avatar,
  Divider,
  Rating,
  Box,
} from '@mui/material';
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import { format } from 'date-fns';
import notFoundImg from "assets/not_found.png";
import { FiX } from 'react-icons/fi';

const getAge = (birthDate) => {
  if (!birthDate) return 'N/A';
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

const Section = ({ title, children }) => (
  <VuiBox mb={3}>
    <VuiTypography variant="button" color="text" fontWeight="bold" mb={1}>
      {title}
    </VuiTypography>
    <VuiBox
      sx={{
        background: 'rgba(255,255,255,0.02)',
        borderRadius: '12px',
        p: 2,
      }}
    >
      {children}
    </VuiBox>
  </VuiBox>
);

function ClientDetailsModal({ open, onClose, client }) {
  const { t } = useTranslation();
  
  if (!client) return null;

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm');
    } catch (error) {
      return dateString;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: '#191932',
          color: '#fff',
          borderRadius: '18px',
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
        },
      }}
    >
      <VuiBox
        display="flex"
        alignItems="center"
        gap={3}
        px={4}
        pt={4}
        pb={2}
        sx={{
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          background: 'linear-gradient(90deg, #22223a 60%, #1a1a2e 100%)',
          borderTopLeftRadius: '18px',
          borderTopRightRadius: '18px',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 10,
            display: 'flex',
            gap: 1,
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              cursor: 'pointer',
              color: '#fff',
              background: 'rgba(34,34,58,0.8)',
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s',
              '&:hover': {
                background: '#2196f3',
                color: '#fff',
              },
            }}
            onClick={onClose}
            title={t('common.cancel')}
          >
            <FiX size={24} />
          </Box>
        </Box>
        <Avatar
          src={client.profile_photo || notFoundImg}
          alt={`${client.first_name} ${client.last_name}`}
          sx={{ width: 72, height: 72, border: '3px solid #353570' }}
        />
        <VuiBox>
          <VuiTypography variant="h4" color="white" fontWeight="bold" mb={0.5}>
            {client.first_name} {client.last_name}
          </VuiTypography>
          <VuiTypography variant="caption" color="text" fontWeight="regular" style={{direction:"ltr"}}>
            {client.phone}
          </VuiTypography>
        </VuiBox>
      </VuiBox>

      <DialogContent sx={{ px: 4, pt: 3, pb: 0 }}>
        <Section title={t('client_table.personal_info')}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <VuiTypography variant="caption" color="text" fontWeight="medium">
                {t('client_table.gender')}
              </VuiTypography>
              <VuiTypography variant="button" color="white" fontWeight="bold" sx={{ ml: 1 }}>
                {client.gender ? t(`client_table.${client.gender.toLowerCase()}`) : t('common.na')}
              </VuiTypography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <VuiTypography variant="caption" color="text" fontWeight="medium">
                {t('client_table.age')}
              </VuiTypography>
              <VuiTypography variant="button" color="white" fontWeight="bold" sx={{ ml: 1 }}>
                {getAge(client.birth_date)}
              </VuiTypography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <VuiTypography variant="caption" color="text" fontWeight="medium">
                {t('client_table.created_at')}
              </VuiTypography>
              <VuiTypography variant="button" color="white" fontWeight="bold" sx={{ ml: 1 }}>
                {client.created_at ? formatDate(client.created_at) : t('common.na')}
              </VuiTypography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <VuiTypography variant="caption" color="text" fontWeight="medium">
                {t('client_table.last_login')}
              </VuiTypography>
              <VuiTypography variant="button" color="white" fontWeight="bold" sx={{ ml: 1 }}>
                {client.updated_at ? formatDate(client.updated_at) : t('common.na')}
              </VuiTypography>
            </Grid>
          </Grid>
        </Section>

        {client.ratings && client.ratings.length > 0 && (
          <Section title={t('client_table.ratings_reviews')}>
            <VuiBox mb={2} display="flex" alignItems="center" gap={2}>
              <Rating value={client.rating} readOnly precision={0.5} size="medium" />
              <VuiTypography variant="button" color="white" fontWeight="bold">
                {client.rating ? client.rating.toFixed(1) : t('common.na')}
              </VuiTypography>
              <VuiTypography variant="caption" color="text">
                ({client.rating_count || 0} {t('client_table.reviews')})
              </VuiTypography>
            </VuiBox>
            {client.ratings.map((rating, index) => (
              <VuiBox key={index} mb={2} p={2} display="flex" alignItems="flex-start" gap={2} sx={{ background: 'rgba(255,255,255,0.04)', borderRadius: 2 }}>
                <Avatar
                  src={rating.driver?.profile_photo || notFoundImg}
                  alt={rating.driver ? `${rating.driver.first_name} ${rating.driver.last_name}` : t('driver_table.driver')}
                  sx={{ width: 40, height: 40, border: '2px solid #353570' }}
                />
                <VuiBox flex={1}>
                  <VuiBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <VuiTypography variant="button" color="white" fontWeight="bold">
                      {rating.driver ? `${rating.driver.first_name} ${rating.driver.last_name}` : t('driver_table.driver')}
                    </VuiTypography>
                    <VuiTypography variant="caption" color="text">
                      {formatDate(rating.created_at)}
                    </VuiTypography>
                  </VuiBox>
                  <VuiBox display="flex" alignItems="center" gap={1} mt={0.5} mb={0.5}>
                    <Rating value={rating.rating_value} readOnly size="small" />
                  </VuiBox>
                  <VuiTypography variant="caption" color="white">
                    {rating.review}
                  </VuiTypography>
                </VuiBox>
              </VuiBox>
            ))}
          </Section>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ClientDetailsModal; 